/**
 * Build-time configuration, validated once at module init.
 *
 * The SPA is a static bundle now: there is no backend to ask at runtime, so
 * every environment-specific value is baked in by Vite at build time and one
 * `dist/` exists per environment. Changing the insights-api origin means a
 * rebuild and redeploy, not a config edit.
 *
 * **Why validate here rather than at the point of use.** A missing `VITE_*`
 * arrives as `undefined`, which stringifies into a URL as the literal
 * `"undefined"` — so the first symptom is a 404 against
 * `https://undefined/api/tenant/insights` from somewhere deep in a component,
 * long after the actual mistake (a build run without the variable set). Failing
 * at import time puts the error next to its cause, and the exported type is
 * `string` rather than `string | undefined` so no call site needs a guard.
 *
 * This replaces the old `/api/embed/runtime-config` round trip, which existed
 * only because the SPA couldn't know its own API origin without asking its
 * backend. With the backend gone, so is the question.
 */

/**
 * Read a required `VITE_*` variable, or throw.
 *
 * Vite statically replaces `import.meta.env.VITE_FOO` at build time, so these
 * must be spelled out literally — a computed lookup like
 * `import.meta.env[name]` is *not* substituted and would be `undefined` in a
 * production build while appearing to work in dev. That is why the caller
 * passes the value in rather than the name.
 */
const required = (name: string, value: string | undefined): string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `${name} is not set. The dashboard is a static bundle and reads its ` +
        `configuration at build time — set ${name} in the build environment ` +
        `and rebuild. See dashboard-app/DEPLOY.md.`,
    );
  }
  return value.trim();
};

/**
 * Absolute origin of insights-api, with any trailing slash removed so callers
 * can concatenate a leading-slash path without producing a double slash.
 *
 * Every authenticated call goes here cross-origin with the session cookie
 * attached; the login flow lives at `/api/tenant/auth/*` on this same origin.
 */
export const INSIGHTS_API_URL: string = required(
  "VITE_INSIGHTS_API_URL",
  import.meta.env.VITE_INSIGHTS_API_URL as string | undefined,
).replace(/\/+$/, "");

/**
 * Header name that must accompany every state-changing cookie-authenticated
 * request.
 *
 * The value is irrelevant — its *presence* is what forces the browser into a
 * CORS preflight, which insights-api's origin allow-list then governs. A
 * forged `<form>` or `<img>` cannot set a custom header.
 *
 * Not configurable per environment on purpose: it has to match
 * `INSIGHTS_CSRF_HEADER` on the API side, and a mismatch fails every write
 * with a 403 that reads like a CORS problem. Kept as a constant here so the
 * coupling is visible rather than buried in a build variable.
 */
export const CSRF_HEADER = "x-lucent-csrf";
